/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Pavel Reznak (reznak@cadwork.cz)
 */

// jscs:disable requireCamelCaseOrUpperCaseIdentifiers

import Pass from "App/render/webgl/passes/pass";
import WebGLRenderGraph from "App/render/webgl/render_graph";

import ShadowMapVarianceShader from "App/render/webgl/shaders/shadow_map_variance/shadow_map_variance"
import View from "App/view/view";
import {Framebuffer} from "App/utils/frame_buffer";
import {Containers, Factory} from "App/utils/factory";
import {
    OpaqueBinTypeName,
    OpaqueNoTextureBinTypeName
} from "App/render/webgl/nodes/bins/type_names";
import IScene from "js_interface/src/scene_interface";

export default 
class ShadowMapVariance extends Pass {

    public static TypeName: string = "ShadowMapVariance";

    constructor(aSceneAPI: IScene, aRenderGraph: WebGLRenderGraph) {
        super(aSceneAPI, aRenderGraph);

        this.initialize();
    }

    public static create(aSceneAPI: IScene, aRenderGraph: WebGLRenderGraph): ShadowMapVariance {
        return new ShadowMapVariance(aSceneAPI, aRenderGraph);
    }

    /**
     * Initialize values
     */
    public initialize() {
        let shaders = this._renderGraph.getLayer('shadowMaps').getShaders();
        this.attachShaderToBin(OpaqueBinTypeName, shaders.get(ShadowMapVariance.TypeName));
        this.attachShaderToBin(OpaqueNoTextureBinTypeName, shaders.get(ShadowMapVarianceShader.TypeName));
    }

    /**
     * Render depth from light position to aOutputBuffer
     *
     * aOutputBuffer needs only 1 Float value to store (function floatToVec4 in fragment shader)
     *
     * @param {View}              aView
     * @param {Framebuffer} aOutput     Unsigned byte, RGBA         ... floatToVec4
     */
    public render(aView: View, aOutput: Framebuffer) {
        /* jshint camelcase:false */
        this.view = aView;
        this.state.resetRenderNodesFilters();

        /** @type WebGLRenderingContext */
        let gl = this._context.getWebGLRenderingContext();
        let camera = aView.getCamera();
        let size = aOutput.getSize();

        aOutput.bind();

        gl.enable(gl.DEPTH_TEST);
        gl.enable(gl.SCISSOR_TEST);
        gl.viewport(0, 0, size[0], size[1]);
        gl.scissor(0, 0, size[0], size[1]);

        // For 16 bit
        if (this._sceneAPI.isMobile()) {
            // TODO is this Y ok?
            let packFloat = function (f) {
                let value = f * 1024.0;
                return {x: Math.floor(value), y: value % 1};
            };
            let cc = packFloat(1.0);
            gl.clearColor(cc.x, cc.y, cc.x, cc.y);
        } else {
            // For 32 bit
            gl.clearColor(1.0, 1.0, 1.0, 1.0);
        }

        gl.clearDepth(1.0);
        gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);

        // render all the bins related to the geometry pass
        let bins = this._renderGraph.getRenderBins();
        this.state.resetRenderNodesFilters();

        // Set shader index (0 - 32bit, 1 - 16bit)
        let shaderIndex = 0;
        if (this._sceneAPI.isMobile()) {
            shaderIndex = 1;
        }

        this.setActiveShaderRenderableIndex(shaderIndex);
        let light = camera.getLightOrthogonalMatrixSeparate();

        // Orthogonal projection matrix from light
        this.bindShader(OpaqueBinTypeName);

        let uniformLocations = this._activeShaderRenderable.getUniformLocations();
        gl.uniformMatrix4fv(uniformLocations.u_lightViewMatrix, false, light.view);
        gl.uniformMatrix4fv(uniformLocations.u_lightProjMatrix, false, light.projection);
        gl.uniform1f(uniformLocations.u_lightMaxLength, light.maxLength);

        bins.get(OpaqueBinTypeName).render(this);
        bins.get(OpaqueNoTextureBinTypeName).render(this);

        // Orthogonal projection matrix from light
        /*this.bindShader(GroundPlane.TypeName);
        gl.uniformMatrix4fv(this._activeShaderRenderable.uniformLocations.u_lightViewMatrix, false, light.view);
        gl.uniformMatrix4fv(this._activeShaderRenderable.uniformLocations.u_lightProjMatrix, false, light.projection);
        gl.uniform1f(this._activeShaderRenderable.uniformLocations.u_lightMaxLength, light.maxLength);
        bins.get(GroundPlane.TypeName).render(this);
    */
        // Reset clear color and view
        this.view = null;
        gl.clearColor(0.0, 0.0, 0.0, 0.0);
    }
}

Factory.register(Containers.PASSES_SM, ShadowMapVariance);
